import { useContext, useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router";

import { ApiProvider } from "./api-context";
import { Footer } from "./components/footer";
import { Header } from "./components/header";
import { Login } from "./components/login";
import { Logout } from "./components/logout";
import { Navigation, NavigationIcon } from "./components/navigation";
import { ToastProvider } from "./components/toast";
import { useIsMobile } from "./hooks/is-mobile";
import { AddNumberPage } from "./pages/add-number";
import { AddPrefixPage } from "./pages/add-prefix";
import { CheckNumberPage } from "./pages/check-number";
import { DownloadPage } from "./pages/download";
import { EditNumberPage } from "./pages/edit-number";
import { ErrorPage } from "./pages/error";
import { FreeNumberPage } from "./pages/free-number";
import { GeneralHelpPage } from "./pages/help/general";
import { TechnicalUserHelpPage } from "./pages/help/technical-user";
import { ImprintPage } from "./pages/imprint";
import { Institute } from "./pages/institute";
import { ListInstitutes } from "./pages/list-institutes";
import { NotFound } from "./pages/not-found";
import { PasswordReset } from "./pages/password-reset";
import { UploadPage } from "./pages/upload";
import { UserVerify } from "./pages/user-verify";
import { VerifyBlockPage } from "./pages/verify-block";
import { UserSessionContext } from "./use-user-context";

import type { ReactNode } from "react";
import type { RouteObject } from "react-router";

const authenticatedRoutes = (): RouteObject[] => {
  return [
    {
      path: "/",
      element: (
        <PageWrapper>
          <AddNumberPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/add",
      element: (
        <PageWrapper>
          <AddNumberPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/check",
      element: (
        <PageWrapper>
          <CheckNumberPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/edit",
      element: (
        <PageWrapper>
          <EditNumberPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/free",
      element: (
        <PageWrapper>
          <FreeNumberPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/download",
      element: (
        <PageWrapper>
          <DownloadPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/block/verify",
      element: (
        <PageWrapper>
          <VerifyBlockPage />
        </PageWrapper>
      ),
    },
    {
      path: "/numbers/upload",
      element: (
        <PageWrapper>
          <UploadPage />
        </PageWrapper>
      ),
    },
    {
      path: "/administration/institutes/list",
      element: (
        <PageWrapper>
          <ListInstitutes />
        </PageWrapper>
      ),
    },
    {
      path: "/administration/institutes/:action",
      element: (
        <PageWrapper>
          <Institute />
        </PageWrapper>
      ),
    },
    {
      path: "/administration/prefix/add",
      element: (
        <PageWrapper>
          <AddPrefixPage />
        </PageWrapper>
      ),
    },
    {
      path: "/help/general",
      element: (
        <PageWrapper>
          <GeneralHelpPage />
        </PageWrapper>
      ),
    },
    {
      path: "/help/technical-user",
      element: (
        <PageWrapper>
          <TechnicalUserHelpPage />
        </PageWrapper>
      ),
    },
    {
      path: "/imprint",
      element: (
        <PageWrapper>
          <ImprintPage />
        </PageWrapper>
      ),
    },
    {
      path: "*",
      element: (
        <PageWrapper>
          <NotFound />
        </PageWrapper>
      ),
    },
    {
      path: "/error",
      element: (
        <PageWrapper>
          <ErrorPage />
        </PageWrapper>
      ),
    },
    {
      path: "/logout",
      element: (
        <PageWrapper>
          <Logout />
        </PageWrapper>
      ),
    },
  ];
};

const PageWrapper = ({
  children,
}: {
  children: React.ReactNode;
}): ReactNode => {
  const [session, setUserSession] = useContext(UserSessionContext);

  const isMobile = useIsMobile();
  const [showNavigation, setShowNavigation] = useState(false);

  useEffect(() => {
    setShowNavigation(!isMobile);
  }, [isMobile]);

  if (!session || !session.active) {
    return <ErrorPage />;
  }

  return (
    <ApiProvider session={session}>
      <ToastProvider>
        <div className="app-container">
          <NavigationIcon
            showNavigation={showNavigation}
            setShowNavigation={setShowNavigation}
          />
          <Header />
          {showNavigation ? (
            <Navigation userSession={session} setUserSession={setUserSession} />
          ) : null}
          <main>{children}</main>
          <Footer />
        </div>
      </ToastProvider>
    </ApiProvider>
  );
};

const notAuthenticatedRoutes = (): RouteObject[] => {
  return [
    {
      path: "/",
      element: <Login />,
    },
    {
      path: "/user/verify",
      element: <UserVerify />,
    },
    {
      path: "/user/password/confirm",
      element: <PasswordReset />,
    },
    {
      path: "/error",
      element: <ErrorPage />,
    },
    {
      path: "*",
      element: <NotFound />,
    },
  ];
};

export const Main: React.FC<object> = () => {
  const [session] = useContext(UserSessionContext);

  if (session === null) {
    return <h2>loading....</h2>;
  }

  const router = createBrowserRouter(
    session && session.active
      ? authenticatedRoutes()
      : notAuthenticatedRoutes(),
  );

  return session && session.active ? (
    <RouterProvider router={router} />
  ) : (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  );
};
